import { useEffect, useMemo, useState } from "react";
import { useAdminTranslation } from "@/i18n/hooks";
import { Heading } from "@/components/tailwind/heading";
import { Button } from "@/components/tailwind/button";
import { Input } from "@/components/tailwind/input";
import { Select } from "@/components/tailwind/select";
import { Text } from "@/components/tailwind/text";
import { ArrowLeft, Plus, Trash, Search } from "lucide-react";
import { District, DistrictStreet } from "@/models/district";
import LoadingSpinner from "@/components/loader-spinner";
import { fetchAllDistricts } from "@/services/district-services";
import {
    createDistrictStreet,
    deleteDistrictStreet,
    fetchAllDistrictStreets,
    updateDistrictStreetSortCode,
} from "@/services/district-street-services";
import { useAuth } from "@/context/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { InputGroup } from "@/components/tailwind/input";
import { Field, Label } from "@/components/tailwind/fieldset";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable, sortableColumnHeader } from "@/components/data-table";
import InlineEditableCell from "@/components/inline-editable-cell";
import log from '@/lib/logger'

export default function DistrictStreetsPage() {
    const { t } = useAdminTranslation();
    const { token } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();

    const [streetMappings, setStreetMappings] = useState<DistrictStreet[]>([]);
    const [districts, setDistricts] = useState<District[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterDistrictId, setFilterDistrictId] = useState<string>("");

    const [newStreet, setNewStreet] = useState("");
    const [newDistrictId, setNewDistrictId] = useState("");
    const [newSortCode, setNewSortCode] = useState("");

    function showToast(title: string, description: string, variant: "default" | "destructive" = "default") {
        toast({ title, description, variant });
    }

    async function loadData() {
        setIsLoading(true);
        try {
            const [mappingsResult, districtsResult] = await Promise.all([
                fetchAllDistrictStreets(token),
                fetchAllDistricts(token),
            ]);
            if (mappingsResult) setStreetMappings(mappingsResult);
            if (districtsResult) setDistricts(districtsResult);
        } catch (error) {
            log.error("Error loading district street data:", error);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        loadData();
    }, [token]);

    const filteredMappings = useMemo(() => {
        let result = streetMappings;
        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            result = result.filter((m) => m.street.toLowerCase().includes(term));
        }
        if (filterDistrictId) {
            result = result.filter((m) => m.districtId === filterDistrictId);
        }
        return result;
    }, [streetMappings, searchTerm, filterDistrictId]);

    async function handleSortCodeUpdate(id: string, sortCode: string | null) {
        try {
            await updateDistrictStreetSortCode(token, id, { sortCode });
            setStreetMappings(prev =>
                prev.map(m => m.id === id ? { ...m, sortCode: sortCode ?? undefined } : m)
            );
        } catch (error) {
            log.error("Error updating sort code:", error);
            showToast(
                t("adminSettings.districtStreets.toast.error.title"),
                t("adminSettings.districtStreets.toast.error.description"),
                "destructive"
            );
        }
    }

    async function handleCreate() {
        if (!newStreet.trim()) return;
        if (!newDistrictId) return;

        setIsLoading(true);
        try {
            await createDistrictStreet(token, {
                districtId: newDistrictId,
                street: newStreet.trim(),
                sortCode: newSortCode.trim() || undefined,
            });
            showToast(
                t("adminSettings.districtStreets.toast.created.title"),
                t("adminSettings.districtStreets.toast.created.description")
            );
            setNewStreet("");
            setNewDistrictId("");
            setNewSortCode("");
            setIsAdding(false);
            await loadData();
        } catch (error) {
            log.error("Error creating district street:", error);
            showToast(
                t("adminSettings.districtStreets.toast.error.title"),
                t("adminSettings.districtStreets.toast.error.description"),
                "destructive"
            );
        } finally {
            setIsLoading(false);
        }
    }

    async function handleDelete(id: string) {
        setIsLoading(true);
        try {
            await deleteDistrictStreet(token, id);
            showToast(
                t("adminSettings.districtStreets.toast.deleted.title"),
                t("adminSettings.districtStreets.toast.deleted.description")
            );
            await loadData();
        } catch (error) {
            log.error("Error deleting district street:", error);
            showToast(
                t("adminSettings.districtStreets.toast.error.title"),
                t("adminSettings.districtStreets.toast.error.description"),
                "destructive"
            );
        } finally {
            setIsLoading(false);
        }
    }

    const columns: ColumnDef<DistrictStreet>[] = useMemo(() => [
        {
            accessorKey: "street",
            header: ({ column }) =>
                sortableColumnHeader(column, "adminSettings.districtStreets.table.street", "admin"),
        },
        {
            accessorKey: "districtName",
            header: ({ column }) =>
                sortableColumnHeader(column, "adminSettings.districtStreets.table.district", "admin"),
        },
        {
            accessorKey: "sortCode",
            header: ({ column }) =>
                sortableColumnHeader(column, "adminSettings.districtStreets.table.sortCode", "admin"),
            cell: ({ row }) => (
                <InlineEditableCell
                    value={row.original.sortCode}
                    onSave={(newValue) => handleSortCodeUpdate(row.original.id, newValue)}
                    placeholder={t("adminSettings.districtStreets.table.sortCodePlaceholder")}
                />
            ),
        },
        {
            id: "actions",
            header: () => <span className="sr-only">{t("adminSettings.districtStreets.table.actions")}</span>,
            cell: ({ row }) => (
                <Button
                    plain
                    onClick={() => handleDelete(row.original.id)}
                    title={t("adminSettings.districts.delete")}
                    data-testid={`delete-district-street-${row.original.id}`}
                >
                    <Trash className="h-4 w-4 text-zinc-500 dark:text-zinc-400 hover:text-red-500 dark:hover:text-red-400" />
                </Button>
            ),
        },
    ], [t]);

    return (
        <div className="max-w-4xl">
            <button
                onClick={() => navigate("/admin/settings")}
                className="flex items-center gap-1 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4"
            >
                <ArrowLeft className="h-4 w-4" />
                {t("adminSettings.districtStreets.backToSettings")}
            </button>

            <div className="flex justify-between items-start gap-4 mb-6">
                <div>
                    <Heading>{t("adminSettings.districtStreets.title")}</Heading>
                    <Text className="mt-1 text-sm text-muted-foreground">
                        {t("adminSettings.districtStreets.description")}
                    </Text>
                </div>
                {!isAdding && (
                    <Button
                        outline
                        onClick={() => setIsAdding(true)}
                        disabled={isLoading}
                        data-testid="add-district-street-button"
                        className="whitespace-nowrap"
                    >
                        <Plus className="h-5 w-5 mr-2" data-slot="icon" />
                        {t("adminSettings.districtStreets.addNew")}
                    </Button>
                )}
            </div>

            {isAdding && (
                <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 bg-white dark:bg-zinc-950 mb-6 space-y-3">
                    <Field>
                        <Label>{t("adminSettings.districtStreets.form.street")}</Label>
                        <Input
                            value={newStreet}
                            onChange={(e) => setNewStreet(e.target.value)}
                            placeholder={t("adminSettings.districtStreets.form.streetPlaceholder")}
                            data-testid="new-street-name"
                        />
                    </Field>
                    <Field>
                        <Label>{t("adminSettings.districtStreets.form.district")}</Label>
                        <Select
                            value={newDistrictId}
                            onChange={(e) => setNewDistrictId(e.target.value)}
                            data-testid="new-street-district"
                        >
                            <option value="">{t("adminSettings.districtStreets.form.districtPlaceholder")}</option>
                            {districts.map((d) => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                        </Select>
                    </Field>
                    <Field>
                        <Label>{t("adminSettings.districtStreets.form.sortCode")}</Label>
                        <Input
                            value={newSortCode}
                            onChange={(e) => setNewSortCode(e.target.value)}
                            placeholder={t("adminSettings.districtStreets.form.sortCodePlaceholder")}
                            data-testid="new-street-sort-code"
                        />
                    </Field>
                    <div className="flex gap-2">
                        <Button
                            color="green"
                            onClick={handleCreate}
                            disabled={!newStreet.trim() || !newDistrictId}
                            data-testid="submit-district-street"
                        >
                            {t("adminSettings.districtStreets.form.save")}
                        </Button>
                        <Button
                            outline
                            onClick={() => {
                                setNewStreet("");
                                setNewDistrictId("");
                                setNewSortCode("");
                                setIsAdding(false);
                            }}
                        >
                            {t("adminSettings.districtStreets.form.cancel")}
                        </Button>
                    </div>
                </div>
            )}

            <div className="flex gap-4 mb-4">
                <div className="flex-1">
                    <InputGroup>
                        <Search className="h-4 w-4" data-slot="icon" />
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={t("adminSettings.districtStreets.searchPlaceholder")}
                            data-testid="district-street-search"
                        />
                    </InputGroup>
                </div>
                <div className="w-48">
                    <Select
                        value={filterDistrictId}
                        onChange={(e) => setFilterDistrictId(e.target.value)}
                        data-testid="district-street-filter"
                    >
                        <option value="">{t("adminSettings.districtStreets.allDistricts")}</option>
                        {districts.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </Select>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-8">
                    <LoadingSpinner />
                </div>
            ) : filteredMappings.length === 0 ? (
                <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded">
                    <Text className="text-blue-700 dark:text-blue-300">
                        {t("adminSettings.districtStreets.noItems")}
                    </Text>
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={filteredMappings}
                    resetPageKey={`${searchTerm}|${filterDistrictId}`}
                    data-testid="district-streets-table"
                    initialSorting={[{ id: "street", desc: false }]}
                    getRowTestId={(row) => `district-street-row-${row.id}`}
                />
            )}
        </div>
    );
}
